/** Bilingual (English + Hindi) UI strings with a tiny typed lookup. */
export type Locale = "en" | "hi";

export const LOCALES: Locale[] = ["en", "hi"];

type Dict = Record<string, { en: string; hi: string }>;

const STRINGS: Dict = {
  appName: { en: "Smart Shikka", hi: "स्मार्ट शिक्षा" },
  tagline: { en: "Your own assistant", hi: "आपका अपना सहायक" },
  email: { en: "Email", hi: "ईमेल" },
  password: { en: "Password", hi: "पासवर्ड" },
  newPassword: { en: "New password", hi: "नया पासवर्ड" },
  code: { en: "Verification code", hi: "सत्यापन कोड" },
  login: { en: "Log in", hi: "लॉग इन" },
  signup: { en: "Sign up", hi: "साइन अप" },
  logout: { en: "Log out", hi: "लॉग आउट" },
  forgot: { en: "Forgot password?", hi: "पासवर्ड भूल गए?" },
  toSignup: { en: "Create an account", hi: "नया खाता बनाएँ" },
  toLogin: { en: "Back to login", hi: "लॉगिन पर वापस" },
  desktopDownload: { en: "Get the Windows desktop app", hi: "Windows डेस्कटॉप ऐप डाउनलोड करें" },
  desktopDownloadHelp: {
    en: "Windows installer (setup .exe), separate from the browser's Install Web App option.",
    hi: "Windows installer (setup .exe), browser के Install Web App विकल्प से अलग।",
  },
  desktopDownloadSignedHelp: {
    en: "Verified signed Windows release (Authenticode).",
    hi: "सत्यापित signed Windows release (Authenticode)।",
  },
  desktopDownloadUnavailable: { en: "Signed Windows release is being prepared. Download stays disabled until Authenticode signing is complete.", hi: "Signed Windows release तैयार हो रहा है। Authenticode signing पूरी होने तक डाउनलोड बंद है।" },
  desktopDownloadBlockedTitle: { en: "Windows desktop app", hi: "Windows डेस्कटॉप ऐप" },
  desktopDownloadLabel: { en: "Download for Windows", hi: "Windows के लिए डाउनलोड करें" },
  desktopDownloadSoonHelp: {
    en: "The Windows installer will appear here when ready; this is separate from the browser's Install Web App option.",
    hi: "Release ready होने पर Windows installer यहाँ दिखेगा; यह browser के Install Web App विकल्प से अलग है।",
  },
  browserExtension: { en: "Browser extension", hi: "ब्राउज़र एक्सटेंशन" },
  browserExtensionStatus: { en: "Installation status unavailable", hi: "इंस्टॉलेशन स्थिति अनुपलब्ध" },
  browserExtensionStatusHelp: {
    en: "The browser cannot reliably detect extension installation or pairing state. Pair only through the owner-approved extension flow.",
    hi: "Browser reliably extension installation या pairing state detect नहीं कर सकता। केवल owner-approved extension flow से pair करें।",
  },
  browserExtensionVersion: { en: "Version", hi: "वर्ज़न" },
  browserExtensionSha256: { en: "SHA-256", hi: "SHA-256" },
  browserExtensionInstallMode: { en: "Install mode", hi: "इंस्टॉल मोड" },
  browserExtensionPrivateUnpacked: { en: "Private unpacked load", hi: "Private unpacked load" },
  browserExtensionStore: { en: "Chrome/Edge Web Store", hi: "Chrome/Edge Web Store" },
  browserExtensionDownload: { en: "Download browser extension", hi: "ब्राउज़र एक्सटेंशन डाउनलोड करें" },
  browserExtensionDownloadUnavailable: { en: "Extension release metadata unavailable. A public HTTPS package and SHA-256 must be configured first.", hi: "Extension release metadata अनुपलब्ध है। पहले public HTTPS package और SHA-256 configure करें।" },
  browserExtensionDownloadHelp: {
    en: "Use the published owner package. Verify the SHA-256 after download. Do not install an unverified copy or publish it to a store.",
    hi: "Published owner package उपयोग करें। Download के बाद SHA-256 verify करें। Unverified copy install या store पर publish न करें।",
  },
  browserExtensionSetup: { en: "Setup", hi: "सेटअप" },
  browserExtensionStepOne: { en: "Download the ZIP above and extract it to a folder.", hi: "ऊपर से ZIP डाउनलोड करें और किसी folder में extract करें।" },
  browserExtensionStepTwo: { en: "Open chrome://extensions or edge://extensions, enable Developer mode, then choose Load unpacked and select the extracted folder.", hi: "chrome://extensions या edge://extensions खोलें, Developer mode चालू करें, फिर Load unpacked चुनें और extracted folder select करें।" },
  browserExtensionStepThree: { en: "Open the extension Options page and connect/pair using the owner-approved server flow. Never paste a token into chat or the page.", hi: "Extension Options page खोलें और owner-approved server flow से connect/pair करें। Token को chat या page में paste न करें।" },
  sendCode: { en: "Send code", hi: "कोड भेजें" },
  resetPassword: { en: "Reset password", hi: "पासवर्ड रीसेट करें" },
  channelEmail: { en: "Email", hi: "ईमेल" },
  channelWhatsapp: { en: "WhatsApp", hi: "व्हाट्सऐप" },
  dashboard: { en: "Dashboard", hi: "डैशबोर्ड" },
  owner: { en: "Owner", hi: "ओनर" },
  member: { en: "Member", hi: "सदस्य" },
  chat: { en: "Chat", hi: "चैट" },
  ownerConsole: { en: "Owner console", hi: "ओनर कंसोल" },
  fast: { en: "Fast", hi: "फ़ास्ट" },
  reasoning: { en: "Reasoning", hi: "रीज़निंग" },
  auto: { en: "Auto", hi: "ऑटो" },
  chatUnavailable: { en: "Chat is not available right now.", hi: "चैट अभी उपलब्ध नहीं है।" },
  accounts: { en: "Accounts", hi: "खाते" },
  memberships: { en: "Membership", hi: "सदस्यता" },
  modules: { en: "Modules", hi: "मॉड्यूल" },
  cms: { en: "Content / Legal", hi: "कंटेंट / लीगल" },
  audit: { en: "Audit log", hi: "ऑडिट लॉग" },
  role: { en: "Role", hi: "भूमिका" },
  status: { en: "Status", hi: "स्थिति" },
  save: { en: "Save", hi: "सेव" },
  saved: { en: "Saved", hi: "सेव हो गया" },
  enabled: { en: "Enabled", hi: "चालू" },
  disabled: { en: "Disabled", hi: "बंद" },
  title: { en: "Title", hi: "शीर्षक" },
  subtitle: { en: "Subtitle", hi: "उपशीर्षक" },
  body: { en: "Body (markdown)", hi: "सामग्री (markdown)" },
  welcome: { en: "Welcome", hi: "स्वागत है" },
  pendingApproval: { en: "Account created — waiting for owner approval.", hi: "खाता बन गया — ओनर की मंज़ूरी का इंतज़ार।" },
  codeSent: { en: "If this email is registered, a verification code has been sent to it.", hi: "अगर यह ईमेल पंजीकृत है तो उस पर सत्यापन कोड भेजा गया है।" },
  signupClosed: { en: "New signups are closed on this server. Ask the owner to create your account.", hi: "इस सर्वर पर नए खाते अभी बंद हैं। खाते के लिए owner से संपर्क करें।" },
  invalidCreds: { en: "Invalid email or password.", hi: "गलत ईमेल या पासवर्ड।" },
  blocked: { en: "This account is blocked.", hi: "यह खाता ब्लॉक है।" },
  genericError: { en: "Something went wrong. Please try again.", hi: "कुछ गड़बड़ हुई। फिर कोशिश करें।" },
  noAccess: { en: "Owner access only.", hi: "सिर्फ़ ओनर के लिए।" },
  updatedBy: { en: "Updated by", hi: "अपडेट किया" },
  never: { en: "never", hi: "कभी नहीं" },
  loading: { en: "Loading…", hi: "लोड हो रहा है…" },
  emptyList: { en: "Nothing here yet.", hi: "अभी कुछ नहीं।" },
  loginSub: { en: "Sign in to your Smart Shikka account.", hi: "अपने स्मार्ट शिक्षा खाते में साइन इन करें।" },
  signupSub: { en: "Create a new Smart Shikka account.", hi: "नया स्मार्ट शिक्षा खाता बनाएँ।" },
  forgotSub: { en: "Recover access with a verification code.", hi: "सत्यापन कोड से एक्सेस वापस पाएँ।" },
  passwordHint: { en: "Min 8 chars, letters + numbers.", hi: "कम से कम 8 अक्षर, अक्षर + अंक।" },
  showPassword: { en: "Show password", hi: "पासवर्ड दिखाएँ" },
  hidePassword: { en: "Hide password", hi: "पासवर्ड छुपाएँ" },
  console: { en: "Owner console", hi: "ओनर कंसोल" },
  channel: { en: "Channel", hi: "माध्यम" },
  workspace: { en: "Workspace", hi: "वर्कस्पेस" },

  // ---- Shikka CEO Control Center ----
  jarvis: { en: "Shikka CEO Control", hi: "शिक्का सीईओ कंट्रोल" },
  jarvisCenter: { en: "Shikka CEO Control Center", hi: "शिक्का सीईओ कंट्रोल सेंटर" },
  jarvisDesc: {
    en: "Shikka CEO — owner + trusted family only. Every capability is OFF by default; execution stays hard-locked.",
    hi: "शिक्का सीईओ — सिर्फ़ ओनर + भरोसेमंद परिवार। हर क्षमता default OFF; execution हमेशा locked।",
  },
  emergencyStop: { en: "Stop Shikka CEO Now", hi: "शिक्का सीईओ अभी रोकें" },
  emergencyOn: { en: "EMERGENCY STOP ACTIVE — all Shikka CEO actions are blocked.", hi: "इमरजेंसी स्टॉप चालू — सभी शिक्का सीईओ actions ब्लॉक हैं।" },
  emergencyConfirm: { en: "Stop Shikka CEO now? All actions will be blocked until you re-enable.", hi: "शिक्का सीईओ अभी रोकें? दोबारा चालू करने तक सभी actions ब्लॉक रहेंगे।" },
  resumeShikkaCeo: { en: "Clear Emergency Stop & re-enable", hi: "इमरजेंसी स्टॉप हटाएँ और दोबारा चालू करें" },
  resumeConfirm: { en: "Clear the emergency stop and resume Shikka CEO controls?", hi: "इमरजेंसी स्टॉप हटाकर शिक्का सीईओ कंट्रोल दोबारा चालू करें?" },
  presentationBadge: { en: "Presentation Mode", hi: "प्रेज़ेंटेशन मोड" },
  masterSwitch: { en: "Master switch", hi: "मास्टर स्विच" },
  controls: { en: "Controls", hi: "कंट्रोल्स" },
  lockedExec: { en: "Execution locked", hi: "execution locked" },
  lockedNote: { en: "Locked in this phase — cannot be enabled.", hi: "इस चरण में locked — चालू नहीं हो सकता।" },
  jarvisEnabled: { en: "Shikka CEO Enabled", hi: "शिक्का सीईओ चालू" },
  webAccess: { en: "Public Web Access", hi: "पब्लिक वेब एक्सेस" },
  publicSignup: { en: "Public Signup", hi: "पब्लिक साइनअप" },
  newUserApproval: { en: "New User Approval", hi: "नए यूज़र की मंज़ूरी" },
  powershellExecuteDesktop: { en: "PowerShell Execute (Desktop)", hi: "PowerShell चलाएँ (डेस्कटॉप)" },
  powershellExecuteWeb: { en: "PowerShell Execute (Web/PWA)", hi: "PowerShell चलाएँ (वेब/PWA)" },
  terminalExecute: { en: "Terminal / CLI Execute", hi: "टर्मिनल / CLI चलाएँ" },
  browserControl: { en: "Browser Control", hi: "ब्राउज़र कंट्रोल" },
  desktopControl: { en: "Desktop Control", hi: "डेस्कटॉप कंट्रोल" },
  voice: { en: "Voice", hi: "वॉइस" },
  providerRouter: { en: "Provider Router", hi: "प्रोवाइडर राउटर" },
  realExecution: { en: "Real Execution", hi: "रियल एग्ज़ीक्यूशन" },
  presentationMode: { en: "Presentation Mode", hi: "प्रेज़ेंटेशन मोड" },
  dcOff: { en: "Off", hi: "बंद" },
  dcReadonly: { en: "Read-only", hi: "रीड-ओनली" },
  desktopHelp: { en: "PowerShell / Terminal Help", hi: "PowerShell / टर्मिनल हेल्प" },
  desktopHelpDesc: { en: "Describe your task — Shikka explains a command. It is NEVER executed.", hi: "अपना काम लिखें — शिक्षा command समझाएगी। यह कभी चलती नहीं।" },
  helpPrompt: { en: "What do you want to do?", hi: "आप क्या करना चाहते हैं?" },
  explain: { en: "Explain command", hi: "command समझाएँ" },
  suggestion: { en: "Suggested command", hi: "सुझाई गई command" },
  explanation: { en: "Explanation", hi: "व्याख्या" },
  riskNote: { en: "Risk note", hi: "जोखिम नोट" },
  copy: { en: "Copy", hi: "कॉपी" },
  copied: { en: "Copied", hi: "कॉपी हो गया" },
  executeDisabled: { en: "Execute is disabled — explain-only.", hi: "चलाना बंद है — सिर्फ़ समझाना।" },
  liveStatus: { en: "Live status", hi: "लाइव स्थिति" },
  idle: { en: "Idle — no action running.", hi: "खाली — कोई action नहीं चल रहा।" },
  jarvisOff: { en: "Shikka CEO is OFF — turn it on to use controls.", hi: "शिक्का सीईओ बंद है — कंट्रोल इस्तेमाल के लिए चालू करें।" },
  approvalQueue: { en: "Approval Queue", hi: "मंज़ूरी क़तार" },
  actionHistory: { en: "Action History", hi: "action इतिहास" },
  askBefore: { en: "Ask before every action", hi: "हर action से पहले पूछें" },
  makeSubAdmin: { en: "Make super-sub-admin", hi: "सुपर-सब-एडमिन बनाएँ" },
  removeSubAdmin: { en: "Remove role", hi: "भूमिका हटाएँ" },
  subAdmin: { en: "Super-sub-admin", hi: "सुपर-सब-एडमिन" },
  twoFactorTitle: { en: "Two-factor verification", hi: "टू-फैक्टर सत्यापन" },
  twoFactorSub: { en: "Enter the code we emailed you to finish signing in.", hi: "साइन-इन पूरा करने के लिए ईमेल किया गया कोड डालें।" },
  verifyLogin: { en: "Verify & log in", hi: "सत्यापित करें और लॉगिन" },
  assistant: { en: "Assistant", hi: "असिस्टेंट" },
  mobile: { en: "Mobile Sync", hi: "मोबाइल सिंक" },
  assistantDesc: { en: "Chat with Shikka. Responses are streamed from the server when a provider and project are configured.", hi: "शिक्षा से बात करें। जब provider और project configured हों तो जवाब server से stream होंगे।" },
  commands: { en: "Commands", hi: "कमांड" },
  mentions: { en: "Mentions", hi: "मेंशन" },
  sendPreview: { en: "Send is preview-only — executed:false", hi: "भेजना सिर्फ़ preview — executed:false" },
  emailTaken: { en: "This email is already registered. Log in or reset your password instead.", hi: "यह ईमेल पहले से पंजीकृत है। लॉग इन करें या पासवर्ड रीसेट करें।" },
  invalidEmail: { en: "Enter a valid email address.", hi: "मान्य ईमेल पता दर्ज करें।" },
  weakPassword: { en: "Use at least 8 characters with both letters and numbers.", hi: "कम से कम 8 अक्षर इस्तेमाल करें, जिनमें अक्षर और अंक दोनों हों।" },
  backToDashboard: { en: "← Back to dashboard", hi: "← डैशबोर्ड पर वापस" },
  ownerStopButton: { en: "STOP SHIKKA", hi: "शिक्षा रोको" },
  ownerStopActive: { en: "STOPPED — Shikka is halted everywhere", hi: "रुक गया — शिक्षा हर जगह रुकी है" },
  ownerStopIdle: { en: "Running — tap STOP to halt everywhere", hi: "चालू है — हर जगह रोकने के लिए STOP दबाएँ" },
  ownerStopNote: {
    en: "Your desktop app checks this every few seconds and stops immediately. Resume only from the desktop or here.",
    hi: "आपका डेस्कटॉप ऐप इसे हर कुछ सेकंड में चेक करके तुरंत रुक जाता है। दोबारा चालू सिर्फ़ डेस्कटॉप या यहीं से।",
  },

  // ---- Mobile PWA ----
  mobilePair: { en: "Pair Device", hi: "डिवाइस जोड़ें" },
  mobilePairDesc: { en: "Enter the pairing code shown on the desktop owner screen.", hi: "डेस्कटॉप ओनर स्क्रीन पर दिखा pairing code डालें।" },
  mobilePairCode: { en: "Pairing code", hi: "pairing code" },
  mobileQrPaste: { en: "QR / deep link", hi: "QR / deep link" },
  mobileQrPasteHint: { en: "Paste the QR URL or scan result", hi: "QR URL या scan result paste करें" },
  mobileDeviceName: { en: "Device name", hi: "डिवाइस नाम" },
  mobilePaired: { en: "Paired successfully", hi: "जोड़ा गया" },
  mobilePairFailed: { en: "Pairing failed", hi: "जोड़ने में गड़बड़" },
  mobileChat: { en: "Chat", hi: "चैट" },
  mobileChatDesc: { en: "Choose a profile and send a message. The server picks the provider/model.", hi: "profile चुनें और message भेजें। provider/model server चुनता है।" },
  mobileMessage: { en: "Message", hi: "संदेश" },
  mobileSend: { en: "Send", hi: "भेजें" },
  mobileReply: { en: "Reply", hi: "जवाब" },
  mobileProfileFast: { en: "Fast Assistant", hi: "fast assistant" },
  mobileProfileCore: { en: "Core Reasoning", hi: "core reasoning" },
  mobileApprovals: { en: "Pending Approvals", hi: "लंबित मंज़ूरियाँ" },
  mobileApprove: { en: "Approve", hi: "मंज़ूर" },
  mobileReject: { en: "Reject", hi: "रद्द" },
  mobileStatus: { en: "Status", hi: "स्थिति" },
  mobileResult: { en: "Result", hi: "परिणाम" },
  mobileDevices: { en: "Devices", hi: "डिवाइस" },
  mobileRevoke: { en: "Revoke", hi: "रद्द करें" },
  mobileRevokeAll: { en: "Revoke All", hi: "सभी रद्द करें" },
  mobileStop: { en: "Emergency Stop", hi: "इमरजेंसी स्टॉप" },
  mobileStopActive: { en: "Emergency Stop is active — no actions can run.", hi: "इमरजेंसी स्टॉप चालू है — कोई action नहीं चल सकता।" },
  mobileEmergencyStopped: { en: "Emergency Stop is active — controlled actions are paused.", hi: "इमरजेंसी स्टॉप चालू है — controlled actions रुकी हुई हैं।" },
  mobileCeoOnlyCommands: { en: "Remote device commands are available to super-admin and super-sub-admin only.", hi: "Remote device commands केवल super-admin और super-sub-admin के लिए हैं।" },
  mobileCeoOnlyStop: { en: "Only super-admin or super-sub-admin can resume from Emergency Stop.", hi: "इमरजेंसी स्टॉप को resume केवल super-admin या super-sub-admin कर सकते हैं।" },
  mobileOffline: { en: "You are offline. Some features may not work.", hi: "आप offline हैं। कुछ features काम नहीं कर सकते।" },
  mobileBack: { en: "Back", hi: "वापस" },
  mobileOwnerConfirm: { en: "Owner confirmation required", hi: "ओनर मंज़ूरी ज़रूरी" },
  mobileTokenMemory: { en: "Session stays in memory only — reload will clear it.", hi: "session सिर्फ़ memory में रहेगा — reload पर clear हो जाएगा।" },
  mobileNoSecret: { en: "QR, links, and local storage never contain the raw token.", hi: "QR, links और local storage में raw token कभी नहीं होता।" },

  // ---- Web Screen Capture ----
  captureTitle: { en: "Screen Capture", hi: "स्क्रीन कैप्चर" },
  captureDesc: { en: "Capture screenshots and recordings from this browser. Choose where each file is saved.", hi: "इस ब्राउज़र से screenshot और recording लें। हर file कहाँ save होगी यह चुनें।" },
  captureSaveTarget: { en: "Save target", hi: "सेव लक्ष्य" },
  captureTargetDownload: { en: "Browser download folder", hi: "ब्राउज़र download folder" },
  captureTargetFile: { en: "Ask for save file each time", hi: "हर बार save file पूछें" },
  captureTargetDirectory: { en: "Save to chosen folder", hi: "चुने गए folder में save करें" },
  captureFilenamePrefix: { en: "Filename prefix", hi: "फ़ाइलनाम prefix" },
  captureFolder: { en: "Save folder", hi: "सेव folder" },
  captureNoFolder: { en: "No folder selected", hi: "कोई folder चुना नहीं गया" },
  captureChooseFolder: { en: "Choose folder", hi: "Folder चुनें" },
  captureScreenshot: { en: "Take screenshot", hi: "Screenshot लें" },
  captureStartRecording: { en: "Start recording", hi: "Recording शुरू करें" },
  captureStopRecording: { en: "Stop recording", hi: "Recording रोकें" },
  captureSaved: { en: "Saved", hi: "सेव हो गया" },
  captureNotSaved: { en: "Not saved", hi: "सेव नहीं हुआ" },
  captureCancelled: { en: "Cancelled", hi: "रद्द हो गया" },
  captureError: { en: "Capture failed", hi: "कैप्चर fail हुआ" },
  captureNotSupported: { en: "Your browser does not support this save API.", hi: "आपका ब्राउज़र यह save API support नहीं करता।" },
  captureChooseFolderNote: { en: "Folder access may need to be re-granted after reload.", hi: "Reload के बाद folder access दोबारा देना पड़ सकता है।" },

  // ---- Assistant sidebar ----
  assistantTitle: { en: "Assistant", hi: "असिस्टेंट" },
  worklog: { en: "Worklog", hi: "Worklog" },
  changes: { en: "Changes", hi: "Changes" },
  browser: { en: "Browser", hi: "Browser" },
  shell: { en: "Shell", hi: "Shell" },
  ide: { en: "IDE", hi: "IDE" },
  agents: { en: "Agents", hi: "Agents" },
  noProvider: { en: "No AI provider configured. Add a provider key in Workspace to send messages.", hi: "कोई AI provider configured नहीं। Messages भेजने के लिए Workspace में provider key जोड़ें।" },
  noProjectContext: { en: "No project selected. Open Workspace and select a project first.", hi: "कोई project selected नहीं। पहले Workspace खोलकर project चुनें।" },
  noSidebarData: { en: "No activity yet.", hi: "अभी कोई activity नहीं।" },
  browserControlStatus: { en: "Browser control", hi: "Browser control" },
  desktopControlStatus: { en: "Desktop control", hi: "Desktop control" },
  explainCommand: { en: "Explain command", hi: "command समझाएँ" },
  commandHint: { en: "Describe what you want to do…", hi: "बताएँ आप क्या करना चाहते हैं…" },
  shellHelpWarning: { en: "This explains only — commands are never executed.", hi: "यह सिर्फ़ समझाता है — commands कभी execute नहीं होतीं।" },
  agentsDesc: { en: "Agent activity from mobile pairing and Cowork runs.", hi: "Mobile pairing और Cowork runs से agent activity।" },
  ideDesc: { en: "Code workspace requires a project. Open the Workspace tab.", hi: "Code workspace के लिए project चाहिए। Workspace tab खोलें।" },
  changesDesc: { en: "Recent code-control activity.", hi: "हाल की code-control activity।" },

  // ---- Web Capture Preview ----
  capturePreview: { en: "Preview", hi: "Preview" },
  captureSource: { en: "Source", hi: "Source" },
  captureTimestamp: { en: "Timestamp", hi: "Timestamp" },
  captureSize: { en: "Size", hi: "Size" },
  captureDuration: { en: "Duration", hi: "Duration" },
  captureSave: { en: "Save", hi: "सेव" },
  captureSaveAs: { en: "Save As", hi: "Save As" },
  captureDiscard: { en: "Discard", hi: "Discard" },
  captureSessionOnly: { en: "Session-only preview — it will be lost on reload.", hi: "Session-only preview — reload पर खो जाएगा।" },
  captureOpenFolder: { en: "Open folder", hi: "Folder खोलें" },
  captureDeleteLocal: { en: "Delete local copy", hi: "Local copy delete करें" },

  // ---- Shared nav / chat ----
  newChat: { en: "New chat", hi: "नई चैट" },
  recentChats: { en: "Recent chats", hi: "हाल की चैट्स" },
  anonymousChat: { en: "Untitled chat", hi: "अनाम चैट" },
  privateAccount: { en: "Private account", hi: "निजी खाता" },
  language: { en: "Switch language", hi: "भाषा बदलें" },
  projects: { en: "Projects", hi: "प्रोजेक्ट्स" },
  files: { en: "Files", hi: "फ़ाइलें" },
  automations: { en: "Automations", hi: "ऑटोमेशन" },
  apps: { en: "Apps", hi: "ऐप्स" },
  skills: { en: "Skills", hi: "स्किल्स" },
  settings: { en: "Settings", hi: "सेटिंग्स" },
  general: { en: "General", hi: "सामान्य" },
  capabilities: { en: "Capabilities", hi: "क्षमताएं" },
  appearance: { en: "Appearance", hi: "दिखावट" },
  memory: { en: "Memory", hi: "स्मृति" },
  comingSoon: { en: "Coming soon", hi: "जल्द आ रहा है" },
  goToChat: { en: "Go to chat", hi: "चैट पर जाएं" },
  welcomeTitle: { en: "What can I do for you today?", hi: "आज मैं आपके लिए क्या करूँ?" },
  welcomeSubtitle: { en: "Type, speak, or attach a file.", hi: "टाइप करें, बोलें, या फ़ाइल जोड़ें।" },
  composerPlaceholder: { en: "Ask Smart Shikka…", hi: "Smart Shikka से पूछें…" },
  attachFile: { en: "Attach file", hi: "फ़ाइल जोड़ें" },
  voiceInput: { en: "Voice input", hi: "आवाज़ से लिखें" },
  sendMessage: { en: "Send", hi: "भेजें" },
  listening: { en: "Listening…", hi: "सुन रहा है…" },
  voiceNotSupported: { en: "Voice input is not supported in this browser.", hi: "इस ब्राउज़र में आवाज़ से लिखना समर्थित नहीं है।" },
  fileTooLarge: { en: "File is too large.", hi: "फ़ाइल बहुत बड़ी है।" },
  fileReadError: { en: "Could not read file.", hi: "फ़ाइल पढ़ी नहीं जा सकी।" },
  aiDisclaimer: { en: "Smart Shikka is AI and may make mistakes. Please verify important information.", hi: "Smart Shikka AI है और गलतियाँ कर सकता है। कृपया महत्वपूर्ण जानकारी जाँचें।" },
  statusReady: { en: "Smart Shikka is ready", hi: "Smart Shikka तैयार है" },
  statusError: { en: "Connection error", hi: "कनेक्शन त्रुटि" },
  statusRequestError: { en: "Request error", hi: "अनुरोध त्रुटि" },
  suggestionsTitle: { en: "Suggestions", hi: "सुझाव" },
  unsupportedAttachment: { en: "Attachments are not supported yet.", hi: "अभी फ़ाइलें संलग्न नहीं की जा सकतीं।" },

  // ---- Modes / permissions ----
  modelAuto: { en: "Auto", hi: "ऑटो" },
  modelFast: { en: "Fast", hi: "फ़ास्ट" },
  modelReasoning: { en: "Reasoning", hi: "रीज़निंग" },
  permChat: { en: "Chat", hi: "चैट" },
  permRead: { en: "Read", hi: "पढ़ें" },
  permWrite: { en: "Write", hi: "लिखें" },
  permSend: { en: "Send", hi: "भेजें" },
  permAuto: { en: "Auto", hi: "ऑटो" },
  permAsk: { en: "Ask", hi: "पूछें" },
  permBlock: { en: "Block", hi: "रोकें" },
  riskLow: { en: "Low", hi: "कम" },
  riskMedium: { en: "Medium", hi: "मध्यम" },
  riskHigh: { en: "High", hi: "उच्च" },
  temperature: { en: "Temperature", hi: "तापमान" },
  permissions: { en: "Permissions", hi: "अनुमतियाँ" },
  adminPanel: { en: "Admin", hi: "एडमिन" },
  organization: { en: "Organization", hi: "संगठन" },
  members: { en: "Members", hi: "सदस्य" },
  billing: { en: "Billing", hi: "बिलिंग" },
  apiKeys: { en: "API Keys", hi: "API कुंजियाँ" },
  connectors: { en: "Connectors", hi: "कनेक्टर्स" },
  sessionManagement: { en: "Session Management", hi: "सत्र प्रबंधन" },
  droidComputers: { en: "Droid Computers", hi: "ड्रॉइड कंप्यूटर" },
  sessionDefaults: { en: "Session Defaults", hi: "सत्र डिफ़ॉल्ट्स" },
  missionDefaults: { en: "Mission Defaults", hi: "मिशन डिफ़ॉल्ट्स" },
  customModels: { en: "Custom Models", hi: "कस्टम मॉडल" },
  usage: { en: "Usage", hi: "उपयोग" },
  support: { en: "Support", hi: "सहायता" },
  accessDenied: { en: "Access denied", hi: "पहुँच अस्वीकृत" },

};

export function t(key: keyof typeof STRINGS, locale: Locale): string {
  const entry = STRINGS[key];
  return entry ? entry[locale] : String(key);
}

export type TranslateKey = keyof typeof STRINGS;
